import { lazy, Suspense, type ComponentProps } from "react";

const SimpleTrendChartLazy = lazy(() =>
  import("./SimpleTrendChart").then((mod) => ({ default: mod.SimpleTrendChart })),
);

const SimpleDonutChartLazy = lazy(() =>
  import("./SimpleDonutChart").then((mod) => ({ default: mod.SimpleDonutChart })),
);

function TrendChartSkeleton() {
  return (
    <div
      className="h-[200px] w-full animate-pulse rounded-md bg-slate-100"
      aria-hidden
    />
  );
}

function DonutChartSkeleton() {
  return (
    <div className="flex flex-col sm:flex-row items-center gap-4">
      <div
        className="h-[140px] w-full sm:w-[140px] shrink-0 animate-pulse rounded-full bg-slate-100"
        aria-hidden
      />
      <div className="flex-1 space-y-2 w-full">
        <div className="h-3 w-3/4 animate-pulse rounded bg-slate-100" />
        <div className="h-3 w-2/3 animate-pulse rounded bg-slate-100" />
        <div className="h-3 w-1/2 animate-pulse rounded bg-slate-100" />
      </div>
    </div>
  );
}

type TrendChartProps = ComponentProps<typeof SimpleTrendChartLazy>;
type DonutChartProps = ComponentProps<typeof SimpleDonutChartLazy>;

export function LazySimpleTrendChart({ data, ...props }: TrendChartProps) {
  if (data.length === 0) {
    return <p className="text-xs text-slate-500 py-8 text-center">No trend data yet</p>;
  }

  return (
    <Suspense fallback={<TrendChartSkeleton />}>
      <SimpleTrendChartLazy data={data} {...props} />
    </Suspense>
  );
}

export function LazySimpleDonutChart({
  data,
  emptyLabel = "No data yet",
  ...props
}: DonutChartProps) {
  const filtered = data.filter((d) => d.value > 0);
  const total = filtered.reduce((sum, d) => sum + d.value, 0);

  if (total === 0) {
    return <p className="text-xs text-slate-500 py-6 text-center">{emptyLabel}</p>;
  }

  return (
    <Suspense fallback={<DonutChartSkeleton />}>
      <SimpleDonutChartLazy data={data} emptyLabel={emptyLabel} {...props} />
    </Suspense>
  );
}
